import { useGameStore } from '@/store/gameStore'
import { BuildingType } from '@/config/buildingConfig'
import { MEMBERSHIP_TIERS, Membership, calculateMembershipIncome } from '@/config/membershipConfig'
import { SectionDeck, IconTile, StatStrip } from '../components/CourtVision'
import { Users, User, Star, Gem, Crown, Lock, IdCard, type LucideIcon } from 'lucide-react'

const TIER_ICONS: Record<string, LucideIcon> = {
  basic: User,
  standard: Star,
  premium: Gem,
  vip: Crown,
}

export function MembershipsTabContent() {
  const { courts, reputation, hasBuilding, enabledMembershipTiers, memberCounts, toggleMembershipTier } = useGameStore()

  const canEnableTier = (tier: Membership): { can: boolean; reason?: string } => {
    for (const req of tier.requirements) {
      if (req.type === 'reputation' && reputation < (req.value as number))
        return { can: false, reason: `Reputación mínima ${req.value}` }
      if (req.type === 'building' && !hasBuilding(req.value as BuildingType))
        return { can: false, reason: `Requiere ${req.value}` }
      if (req.type === 'courts' && courts.length < (req.value as number))
        return { can: false, reason: `Mínimo ${req.value} pistas` }
    }
    return { can: true }
  }

  const monthlyIncome = calculateMembershipIncome(memberCounts)
  const totalMembers = Object.values(memberCounts).reduce((s, c) => s + c, 0)

  return (
    <div>
      {monthlyIncome > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <StatStrip
            label="Ingresos por cuotas"
            value={<span className="money-positive">{monthlyIncome.toLocaleString('es-ES')}€/mes</span>}
            suffix={<IconTile icon={Users} tone="success" />}
          />
        </div>
      )}

      <SectionDeck title="Cuotas de socios" icon={IdCard} meta={totalMembers > 0 ? `${totalMembers} socios` : undefined}>
        <div className="space-y-2">
          {MEMBERSHIP_TIERS.map(tier => {
            const { can, reason } = canEnableTier(tier)
            const isEnabled = enabledMembershipTiers.includes(tier.id)
            const memberCount = memberCounts[tier.id] || 0
            const TierIcon = TIER_ICONS[tier.id] ?? User

            if (!can) {
              return (
                <div key={tier.id} className="row-tile" style={{ opacity: 0.6 }}>
                  <IconTile icon={TierIcon} tone="muted" />
                  <div className="row-tile-main">
                    <div className="row-tile-title">{tier.name}</div>
                    <div className="row-tile-sub">{tier.monthlyFee}€/mes por socio</div>
                  </div>
                  <span className="pill pill-warning"><Lock size={10} /> {reason}</span>
                </div>
              )
            }

            return (
              <div key={tier.id} className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.55rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <IconTile icon={TierIcon} tone={isEnabled ? 'success' : 'muted'} />
                  <div className="row-tile-main">
                    <div className="row-tile-title">
                      {tier.name}
                      {isEnabled && <span className="pill pill-success">Activa</span>}
                    </div>
                    <div className="row-tile-sub">{tier.monthlyFee}€/mes por socio</div>
                  </div>
                  <div className="row-tile-side">
                    {isEnabled && (
                      <span className="money-positive" style={{ fontWeight: 700 }}>
                        {(memberCount * tier.monthlyFee).toLocaleString('es-ES')}€/mes
                      </span>
                    )}
                    <button
                      onClick={() => toggleMembershipTier(tier.id)}
                      className={isEnabled ? 'btn btn-success' : 'btn btn-secondary'}
                      style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }}
                    >
                      {isEnabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>

                {isEnabled && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    borderTop: '1px solid var(--border-default)', paddingTop: '0.5rem',
                  }}>
                    <span className="meta-text" style={{ minWidth: '54px' }}>Ocupación</span>
                    <div className="progress-track" style={{ flex: 1 }}>
                      <div
                        className="progress-fill angled"
                        style={{ width: `${(memberCount / tier.maxMembers) * 100}%` }}
                      />
                    </div>
                    <span className="meta-text" style={{ minWidth: '58px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {memberCount}/{tier.maxMembers}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </SectionDeck>
    </div>
  )
}
